import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter, inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {of, Subject, switchMap} from 'rxjs';
import {takeUntil, tap} from 'rxjs/operators';
import {MatSelectChange, MatSelect} from "@angular/material/select";
import {compareWith, deepCopy} from "../../../lib/utils";
import {Observable} from 'rxjs';
import {SelectSearchApiService} from '../select-search-api.service';
import {SELECT_SEARCH_PLACEHOLDER, TOOLTIP_SHOW_DELAY} from "../../../shared/globals";
import {ConfigStub} from '../../../_typing/config-stub';
import {OptionListSearchComponent} from "../../option-list-search/option-list-search.component";
import {Account} from "../../../_models/account";
import {MatSelectInfiniteScrollDirective} from "../mat-infinite/mat-select-infinite-scroll.directive";
import {DblClickCopyDirective} from "../../../directives/dbl-click-copy.directive";
import {StringFunction, ValueFunction} from "../types";
import {ModelID} from "../../../_typing/generic-types";
import {SharedModule} from "../../../shared/modules/shared-module.module";
import {ProcessMenuDirective} from "../../../directives/process-menu.directive";
import {NameOrDescriptionPipe} from "../../../shared/pipes";
import {ClearConstantService} from "../../constant-field/clear-constant.service";

@Component({
    selector: 'select-search-api',
    templateUrl: 'select-search-api.component.html',
    styleUrls: ['../select-search-api.component.scss', '../many/select-many-search-api.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [SelectSearchApiService],
    imports: [
        SharedModule,
        DblClickCopyDirective,
        OptionListSearchComponent,
        MatSelectInfiniteScrollDirective,
        ProcessMenuDirective
    ],
})
export class SelectSearchApiComponent implements OnInit, AfterViewInit, OnDestroy {
    private onDestroy: Subject<void> = new Subject();
    private _init_value: any;

    @ViewChild('matSelect') matSelect: MatSelect;
    @ViewChild(OptionListSearchComponent) searchComponent!: OptionListSearchComponent;

    @Input() placeholder: string = 'Search';
    @Input() required: boolean = false;
    @Input() openOnInit = false;
    @Input() fetchData: (index: Number, filterValue: String) => Observable<any[]>;
    @Input() label: string = '';
    @Input() classes: string;
    @Input() panelClasses: string;
    @Input() disabled = false;
    @Input() allowAccountSelection: boolean = true;
    @Input() allowedAccountIds: ModelID[];

    @Input() stringFunction: StringFunction;
    @Input() valueFunction: ValueFunction;
    @Input() compareFunctionName;
    @Input() value;
    @Input() allow_none = false;
    @Input() noItemsAvailableMessage: string = "";

    @Input() set init_value(value: any) {
        if (value?.id) {
            this._init_value = new ConfigStub(value);
            this.sapi.options_available.next([this._init_value]);
        }
    }

    get init_value(): any {
        return this._init_value;
    }

    /**
     * For running a function when the selection was changed.
     */
    @Output() selectionChange: EventEmitter<MatSelectChange> = new EventEmitter<MatSelectChange>();
    @Output() closeWithoutChange: EventEmitter<any> = new EventEmitter();
    // For components using onPush change detection when loadedItems list changes
    @Output() showUpdate: EventEmitter<null> = new EventEmitter<null>();
    @Output() accountsChange = new EventEmitter<Account[]>();

    selected_value_id: String = '';
    options$: Observable<{ text: string, value: any }[]>;
    loadedItems: { text: string, value: any }[] = [];
    initialised = false;
    tooltipShowDelay = TOOLTIP_SHOW_DELAY;
    searchText = "";
    selectedTriggerText: string;
    placeholderText: string = SELECT_SEARCH_PLACEHOLDER;
    private clearValueService: ClearConstantService = inject(ClearConstantService);

    constructor(public sapi: SelectSearchApiService,
                private changeDetection: ChangeDetectorRef,
                private nameOrDescription: NameOrDescriptionPipe) {
        this.options$ = this.sapi.connect();
        this.options$.pipe(
            switchMap(list => of(list)),
            tap(list => {
                this.loadedItems = list;
                // For components using onPush change detection
                this.showUpdate.emit();
            })
        ).subscribe(() => {
            this.changeDetection.markForCheck();
        });
        this.clearValueService.clearSelectedRequested
            .pipe(takeUntil(this.onDestroy))
            .subscribe(() => {
                this.value = null;
                this.selectionChange.emit(this.value);
                this.changeDetection.markForCheck()
            });
    }

    ngOnInit() {
        if (!this.label) {
            this.classes += ' no-label';
        }
        this.panelClasses = 'select-search-dropdown-panel ' + (this.panelClasses || '')
    }

    /**Called on onOpenChanged()**/
    initSelectSearch() {
        this.setupItemFunctions();
        this.filterOptions("");
    }

    filterOptions(filterString: string) {
        this.searchText = filterString;
        this.sapi.resetList(this.init_value ? [this.init_value] : []);
        this.getNextBatch();
    }

    ngAfterViewInit() {
        if (this.matSelect && this.openOnInit === true) {
            this.matSelect.open();
        }
    }

    emitSelect(newSelection: MatSelectChange) {
        this.selectionChange.emit(newSelection);
    }

    getNextBatch() {
        this.sapi.getNextBatch(this.fetchData, this.searchText);
    }

    openedChange(e: boolean) {
        if (!this.initialised) {
            this.initialised = true;
            this.initSelectSearch();
        }
        if (e === true) {
            this.searchText = "";
        } else {
            this.closeWithoutChange.emit();
            const value = this.value ? this.loadedItems.find(i => this.compareWith(i.value, this.value)) : null;
            if (value?.value || this.init_value) {
                this.sapi.resetList([value?.value || this.init_value]);
            }
        }
    }

    clearSearch(event) {
        this.filterOptions("");
    }

    onAccountsChanged($event) {
        this.accountsChange.emit($event);
        this.filterOptions(this.searchText);
    }

    setupItemFunctions() {
        /**If value is function is passed in**/
        if (this.stringFunction) {
            this.sapi.stringFunction = this.stringFunction;
        }
        if (this.valueFunction) {
            this.sapi.valueFunction = this.valueFunction;
        }
    }

    getPlaceholder(): string {
        if (!this.value) {
            this.selectedTriggerText = this.disabled ? '' : this.placeholderText;
            return this.selectedTriggerText;
        }
        this.selectedTriggerText = this.stringFunction && this.stringFunction(this.value) ? this.stringFunction(this.value) : this.value.attributes ? (this.nameOrDescription.transform(this.value.attributes)) :
            this.init_value?.attributes ? this.nameOrDescription.transform(this.init_value.attributes) : this.disabled ? '' : this.placeholderText;
        return this.selectedTriggerText;
    }

    compareWith: (option, value) => boolean = (option, value) => {
        return compareWith(this.compareFunctionName, option, value); // utils.compareWith
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.unsubscribe();
    }
}
